import { ParsedUrlQuery } from "querystring";

import { ColumnFiltersState } from "@tanstack/react-table";
import { z } from "zod";

export function encodeColumnFilters(
    filters: ColumnFiltersState,
    columnFiltersCodecMap: Record<string, z.ZodCodec>,
    onError?: (error: unknown) => void
): ParsedUrlQuery {
    if (!filters || !filters.length) {
        return {};
    }

    const encodedString = encodeValuesWithCodecMap(
        filters,
        columnFiltersCodecMap,
        onError
    );
    return encodedString.length > 0
        ? {
              columnFilters: encodedString,
          }
        : {};
}

export function encodeValuesWithCodecMap(
    values: ColumnFiltersState,
    codecMap: Record<string, z.ZodCodec>,
    onError?: (error: unknown) => void
) {
    const valuesMap = Object.fromEntries(values.map((f) => [f.id, f.value]));

    // Encode each value with corresponding codec in codecMap
    const encodedFilterValuesObj = Object.entries<unknown>(valuesMap).reduce(
        (acc, filter) => {
            const [filterId, filterValue] = filter;
            if (filterValue === undefined) {
                return acc;
            }
            const codec = codecMap[filterId];
            if (codec === undefined) {
                const message = `Missing codec for ${filterId}`;
                console.error(message);
                onError?.(message);
                return acc;
            }
            const encodeResult = codec.safeEncode(filterValue);
            if (encodeResult.error) {
                console.error(encodeResult.error);
                onError?.(encodeResult.error);
                return acc;
            }

            return {
                ...acc,
                [filterId]: encodeResult.data,
            };
        },
        {}
    );
    // Encode the object of encoded values into a string
    const encodedObjString = columnFiltersValueCodec.encode(
        encodedFilterValuesObj
    );

    // The string used for param
    return encodedObjString;
}

export function decodeValuesWithCodecMap(
    encodedColFiltersObj: string | string[],
    codecMap: Record<string, z.ZodCodec>
) {
    if (Array.isArray(encodedColFiltersObj)) {
        throw new Error("Didn't expect an array");
    }
    const erroredFilters: Record<string, z.ZodError> = {};

    // Decode the URL string, tracking any skipped (invalid format) filters
    const { filters: decodedColFiltersObj, skippedFilterIds } =
        decodeColumnFiltersValue(encodedColFiltersObj);

    // Mark skipped filters as errored so they get removed from the URL
    skippedFilterIds.forEach((id) => {
        erroredFilters[id] = new z.ZodError([]);
    });

    const decodedFilterValuesObj = Object.entries(decodedColFiltersObj).reduce(
        (acc, filter) => {
            const [filterId, filterValue] = filter;
            const codec = codecMap[filterId];
            if (codec === undefined) {
                console.error(`Missing codec for ${filterId}`);
                erroredFilters[filterId] = new z.ZodError([]);
                return {
                    ...acc,
                    [filterId]: undefined,
                };
            }
            const decodeResult = codec.safeDecode(filterValue);
            if (decodeResult.error) {
                erroredFilters[filterId] = decodeResult.error;
                return {
                    ...acc,
                    [filterId]: undefined,
                };
            }

            return {
                ...acc,
                [filterId]: decodeResult.data,
            };
        },
        {} as Record<string, unknown>
    );
    const decodedFilters: ColumnFiltersState = Object.entries(
        decodedFilterValuesObj
    ).map(([id, value]) => ({ id, value }));
    return { decodedFilters, erroredFilters };
}

// Encodes the structure for the value of the ?columnFilters= search param: a
// flat string of `id-value` pairs joined by `_`, with both components escaped.
const columnFiltersSchema = z.record(z.string(), z.unknown());

const FILTER_ID_DELIMITER = "_";
const COLUMN_MODE_DELIMITER = "-";

// Encode a key or a value, escaping non-URL safe characters & delimiters
function encodeComponent(component: unknown) {
    return encodeURI(component as string)
        .replaceAll(FILTER_ID_DELIMITER, "%5F")
        .replaceAll(COLUMN_MODE_DELIMITER, "%2D");
}

/**
 * Decodes column filters from a URL string.
 * Returns both the decoded filters and any filter IDs that were skipped due to invalid format.
 * Skipped filters should be removed from the URL by the caller.
 */
export function decodeColumnFiltersValue(encString: string): {
    filters: Record<string, unknown>;
    skippedFilterIds: string[];
} {
    const values = encString.split(FILTER_ID_DELIMITER);
    const filters: Record<string, unknown> = {};
    const skippedFilterIds: string[] = [];

    values.forEach((v) => {
        // Split on first "-" only to handle values that may contain "-"
        const delimiterIndex = v.indexOf(COLUMN_MODE_DELIMITER);
        if (delimiterIndex === -1) {
            console.warn(`Skipping invalid filter (no delimiter): ${v}`);
            return;
        }
        const id = v.slice(0, delimiterIndex);
        const value = v.slice(delimiterIndex + 1);
        if (!id) {
            console.warn(`Skipping invalid filter (no columnId): ${v}`);
            return;
        }
        if (!value) {
            console.warn(`Skipping invalid filter (empty value): ${v}`);
            skippedFilterIds.push(decodeURI(id));
            return;
        }
        filters[decodeURI(id)] = decodeURI(value);
    });

    return { filters, skippedFilterIds };
}

export const columnFiltersValueCodec = z.codec(
    z.string(),
    columnFiltersSchema,
    {
        encode: (filters) => {
            const filtersAsString = Object.entries(filters).map(
                ([id, value]) =>
                    `${encodeComponent(
                        id
                    )}${COLUMN_MODE_DELIMITER}${encodeComponent(value)}`
            );
            return filtersAsString.join(FILTER_ID_DELIMITER);
        },
        decode: (encString) => {
            const { filters } = decodeColumnFiltersValue(encString);
            return filters;
        },
    }
);
